import { useCallback, useRef } from 'react'
import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import {
    DETAILS_SYNC_TIMEOUT,
    EMPTY_MONGO_OBJECT_ID,
    GENERAL_TASK_SOURCE_NAME,
    NO_TITLE,
    SINGLE_SECOND_INTERVAL,
    SYNC_MESSAGES,
} from '../../constants'
import { useInterval, useKeyboardShortcut, useNavigateToTask } from '../../hooks'
import useIsReadOnly from '../../hooks/useIsReadOnly'
import { useIsFeatureEnabled } from '../../services/api/feature-flags.hooks'
import { useModifyRecurringTask } from '../../services/api/recurring-tasks.hooks'
import {
    TModifyTaskData,
    useMarkTaskDoneOrDeleted,
    useModifyTask,
    useReorderTask,
} from '../../services/api/tasks.hooks'
import { Colors, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import { TRecurringTaskTemplate, TTaskV4 } from '../../utils/types'
import { isTaskParentTask } from '../../utils/utils'
import GTTextField from '../atoms/GTTextField'
import { Icon } from '../atoms/Icon'
import { MeetingStartText } from '../atoms/MeetingStartText'
import Spinner from '../atoms/Spinner'
import TimeRange from '../atoms/TimeRange'
import ExternalLinkButton from '../atoms/buttons/ExternalLinkButton'
import GTButton from '../atoms/buttons/GTButton'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import { BodySmall } from '../atoms/typography/Typography'
import { useOptionalGoalCreation } from '../goals/creation/shared/GoalCreationContext'
import FolderSelector from '../molecules/FolderSelector'
import GTDatePicker from '../molecules/GTDatePicker'
import GoalSelector from '../molecules/GoalSelector'
import DeleteRecurringTaskTemplateButton from '../molecules/recurring-tasks/DeleteRecurringTaskTemplateButton'
import RecurringTaskDetailsBanner from '../molecules/recurring-tasks/RecurringTaskDetailsBanner'
import RecurringTaskTemplateDetailsBanner from '../molecules/recurring-tasks/RecurringTaskTemplateDetailsBanner'
import RecurringTaskTemplateScheduleButton from '../molecules/recurring-tasks/RecurringTaskTemplateScheduleButton'
import SubtaskList from '../molecules/subtasks/SubtaskList'
import PriorityDropdown from '../radix/PriorityDropdown'
import TaskActionsDropdown from '../radix/TaskActionsDropdown'
import DetailsViewTemplate from '../templates/DetailsViewTemplate'
import RecurrenceSuggestionCard from './RecurrenceSuggestionCard'
import TaskBody from './TaskBody'
import TaskResearchCard from './TaskResearchCard'

const TITLE_MAX_HEIGHT = 208

const DetailsTopContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-basis: 50px;
    flex-shrink: 0;
`
// The task's actions sit on the title's first line, so they stay put while a long title wraps.
const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: ${Spacing._8};
`
const TitleField = styled.div`
    flex: 1;
    min-width: 0;
`
const TitleActions = styled.div`
    display: flex;
    align-items: center;
    /* One line of the title field (24px line + 8px padding + 1px border, top and bottom), so the
       buttons center on the title's first line. */
    height: 42px;
    gap: ${Spacing._4};
    flex-shrink: 0;
`
const DetailItem = styled.div`
    display: flex;
    align-items: center;
    margin-left: ${Spacing._8};
`
const TaskStatusContainer = styled.div`
    display: flex;
    gap: ${Spacing._8};
    align-items: center;
`
const MeetingPreparationTimeContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${Spacing._24};
    margin-left: ${Spacing._8};
    color: ${Colors.text.muted};
    ${Typography.body.small};
    ${Typography.bold};
`
const BackButtonContainer = styled(NoStyleButton)`
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
    color: ${Colors.text.ink};
    ${Typography.label.small};
`
const BackButtonText = styled(BodySmall)`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: inherit;
`

const SOURCES_ALLOWED_WITH_SUBTASKS = [GENERAL_TASK_SOURCE_NAME]

interface TaskDetailsProps {
    task: Partial<TTaskV4> & Partial<TRecurringTaskTemplate> & { id: string; title: string }
    isRecurringTaskTemplate?: boolean
}
const TaskDetails = ({ task, isRecurringTaskTemplate }: TaskDetailsProps) => {
    const [isEditing, setIsEditing] = useState(false)
    const [syncIndicatorText, setSyncIndicatorText] = useState(SYNC_MESSAGES.COMPLETE)

    const { mutate: modifyTask, isError, isLoading } = useModifyTask()
    const { mutate: reorderTask } = useReorderTask()
    const { mutate: modifyRecurringTask } = useModifyRecurringTask()

    const { mutate: markTaskDoneOrDeleted } = useMarkTaskDoneOrDeleted()
    const timers = useRef<{ [key: string]: { timeout: NodeJS.Timeout; callback: () => void } }>({})

    const navigateToTask = useNavigateToTask()

    const [meetingStartText, setMeetingStartText] = useState<string | null>(null)
    const { meeting_preparation_params } = task
    const dateTimeStart = DateTime.fromISO(meeting_preparation_params?.datetime_start || '')
    const dateTimeEnd = DateTime.fromISO(meeting_preparation_params?.datetime_end || '')

    const isMeetingPreparationTask = !!meeting_preparation_params
    const isSubtask = !!task.id_parent
    const goals = useOptionalGoalCreation()
    const isReadOnly = useIsReadOnly()
    const recurrenceSuggestionsEnabled = useIsFeatureEnabled('recurrence_suggestions')
    const researchEnabled = useIsFeatureEnabled('research')

    const titleRef = useRef<HTMLTextAreaElement>(null)

    useInterval(() => {
        if (!meeting_preparation_params) return
        const minutesToStart = Math.ceil(dateTimeStart.diffNow('minutes').minutes)
        const minutesToEnd = Math.ceil(dateTimeEnd.diffNow('minutes').minutes)
        if (minutesToStart < 0 && minutesToEnd > 0) {
            setMeetingStartText('Meeting is now')
        } else if (minutesToStart < 0 && minutesToEnd < 0) {
            setMeetingStartText(null)
        } else if (minutesToStart <= 30) {
            const minutesToStartText = minutesToStart === 1 ? 'minute' : 'minutes'
            setMeetingStartText(`Starts in ${minutesToStart} ${minutesToStartText}`)
        } else {
            setMeetingStartText(null)
        }
    }, SINGLE_SECOND_INTERVAL)

    useEffect(() => {
        if (isEditing || isLoading) {
            setSyncIndicatorText(SYNC_MESSAGES.SYNCING)
        } else if (isError) {
            setSyncIndicatorText(SYNC_MESSAGES.ERROR)
        } else {
            setSyncIndicatorText(SYNC_MESSAGES.COMPLETE)
        }
    }, [isError, isLoading, isEditing])

    useEffect(() => {
        titleRef.current?.addEventListener('focus', () => {
            if (titleRef.current?.value === NO_TITLE) {
                titleRef?.current?.select()
            }
        })
        return () => {
            for (const timer of Object.values(timers.current)) {
                timer.callback()
                clearTimeout(timer.timeout)
            }
        }
    }, [])

    const syncDetails = useCallback(
        ({ id, title, body }: TModifyTaskData) => {
            setIsEditing(false)
            const isEditingTitle = title !== undefined
            if (isEditingTitle && title === '' && titleRef.current) {
                title = NO_TITLE
                titleRef.current.value = NO_TITLE
                if (document.activeElement === titleRef.current) {
                    titleRef.current.select()
                }
            }
            const timerId = id + (title === undefined ? 'body' : 'title')
            if (timers.current[timerId]) clearTimeout(timers.current[timerId].timeout)
            if (isRecurringTaskTemplate) {
                modifyRecurringTask(
                    {
                        id,
                        title,
                        body,
                    },
                    task.optimisticId
                )
            } else {
                modifyTask({ id, title, body }, task.optimisticId)
            }
        },
        [task.id, modifyTask]
    )

    const onEdit = ({ id, title, body }: TModifyTaskData) => {
        setIsEditing(true)
        const timerId = id + (title === undefined ? 'body' : 'title') // we're only modifying the body or title, one at a time
        if (timers.current[timerId]) clearTimeout(timers.current[timerId].timeout)
        timers.current[timerId] = {
            timeout: setTimeout(() => syncDetails({ id, title, body }), DETAILS_SYNC_TIMEOUT),
            callback: () => syncDetails({ id, title, body }),
        }
    }

    useKeyboardShortcut(
        'editTaskName',
        useCallback(() => titleRef.current?.select(), [])
    )

    const navigateToParentTask = useCallback(() => {
        if (isSubtask && task.id_parent) {
            navigateToTask({ taskId: task.id_parent })
        }
    }, [isSubtask, task.id_parent])

    useKeyboardShortcut('backToParentTask', navigateToParentTask)

    const taskv4 = task as TTaskV4

    return (
        <DetailsViewTemplate>
            {isSubtask && (
                <DetailsTopContainer>
                    <DetailItem>
                        <BackButtonContainer onClick={navigateToParentTask}>
                            <Icon icon={icons.caret_left} color="black" />
                            <BackButtonText>Return to parent task</BackButtonText>
                        </BackButtonContainer>
                    </DetailItem>
                </DetailsTopContainer>
            )}
            <TitleRow>
                <TitleField>
                    <GTTextField
                        type="plaintext"
                        ref={titleRef}
                        key={task.id}
                        value={task.is_deleted ? `${task.title} (deleted)` : task.title}
                        disabled={
                            !!task.optimisticId ||
                            isMeetingPreparationTask ||
                            !!task.id_nux_number ||
                            task.is_deleted ||
                            isReadOnly
                        }
                        onChange={(val) => onEdit({ id: task.id, title: val })}
                        maxHeight={TITLE_MAX_HEIGHT}
                        fontSize="medium"
                        hideUnfocusedOutline
                        enterBehavior="blur"
                    />
                </TitleField>
                {!task.optimisticId && (
                    <TitleActions>
                        <BodySmall color="light">{syncIndicatorText}</BodySmall>
                        {task.is_deleted && (
                            <GTButton
                                value="Restore Task"
                                disabled={isReadOnly}
                                onClick={() =>
                                    markTaskDoneOrDeleted(
                                        { id: task.id, isDeleted: false },
                                        task.optimisticId && task.id
                                    )
                                }
                                styleType="secondary"
                            />
                        )}

                        {!isMeetingPreparationTask &&
                            !isRecurringTaskTemplate &&
                            task.id_folder &&
                            !isSubtask &&
                            !task.is_deleted && (
                                <FolderSelector
                                    value={task.id_folder}
                                    onChange={(newFolderId) =>
                                        reorderTask(
                                            {
                                                id: task.id,
                                                dropSectionId: newFolderId,
                                                dragSectionId: task.id_folder,
                                                orderingId: 1,
                                            },
                                            task.optimisticId
                                        )
                                    }
                                    enableKeyboardShortcut
                                    disabled={isReadOnly}
                                />
                            )}
                        {task.deeplink && <ExternalLinkButton link={task.deeplink} />}
                        {!isRecurringTaskTemplate && <TaskActionsDropdown task={taskv4} />}
                        {isRecurringTaskTemplate && (
                            <DeleteRecurringTaskTemplateButton template={task as TRecurringTaskTemplate} />
                        )}
                    </TitleActions>
                )}
            </TitleRow>
            {meeting_preparation_params && (
                <MeetingPreparationTimeContainer>
                    <TimeRange start={dateTimeStart} end={dateTimeEnd} />
                    <MeetingStartText isTextColored>{meetingStartText}</MeetingStartText>
                </MeetingPreparationTimeContainer>
            )}
            <TaskStatusContainer>
                <PriorityDropdown
                    value={task.priority_normalized ?? 0}
                    onChange={(priority) =>
                        isRecurringTaskTemplate
                            ? modifyRecurringTask({ id: task.id, priority_normalized: priority }, task.optimisticId)
                            : modifyTask({ id: task.id, priorityNormalized: priority }, task.optimisticId)
                    }
                    disabled={task.is_deleted || isReadOnly}
                />
                {!isRecurringTaskTemplate && (
                    <GTDatePicker
                        initialDate={DateTime.fromISO(task.due_date ?? '')}
                        setDate={(date) => modifyTask({ id: task.id, dueDate: date })}
                        disabled={task.is_deleted || isReadOnly}
                    />
                )}
                {!isRecurringTaskTemplate && !isMeetingPreparationTask && !isSubtask && goals && (
                    <GoalSelector
                        value={goals.goalIdForTask(task.id, task.optimisticId) ?? null}
                        onChange={(goalId) => goals.linkTaskToGoal(task.id, goalId, task.optimisticId)}
                        disabled={task.is_deleted || isReadOnly}
                    />
                )}
                {isRecurringTaskTemplate ? (
                    <RecurringTaskTemplateScheduleButton templateId={task.id} />
                ) : (
                    task.source?.name === 'General Task' &&
                    task.id_parent === undefined && (
                        <RecurringTaskTemplateScheduleButton
                            templateId={task.recurring_task_template_id}
                            task={taskv4}
                            folderId={task.id_folder}
                        />
                    )
                )}
            </TaskStatusContainer>
            {task.optimisticId ? (
                <Spinner />
            ) : (
                <>
                    {!isRecurringTaskTemplate &&
                        recurrenceSuggestionsEnabled &&
                        taskv4.recurrence_suggestion &&
                        (!taskv4.recurring_task_template_id ||
                            taskv4.recurring_task_template_id === EMPTY_MONGO_OBJECT_ID) &&
                        !isSubtask && <RecurrenceSuggestionCard task={taskv4} />}
                    {/* TODO: remove empty ObjectId check once backend stops giving us empty object ids */}
                    {!isRecurringTaskTemplate &&
                        task.recurring_task_template_id &&
                        task.recurring_task_template_id !== EMPTY_MONGO_OBJECT_ID &&
                        task.id_folder && <RecurringTaskDetailsBanner templateId={task.recurring_task_template_id} />}
                    {isRecurringTaskTemplate && task.id_task_section && (
                        <RecurringTaskTemplateDetailsBanner id={task.id} folderId={task.id_task_section} />
                    )}
                    <TaskBody
                        id={task.id}
                        body={task.body ?? ''}
                        contentType="markdown"
                        onChange={(val) => onEdit({ id: task.id, body: val })}
                        disabled={task.is_deleted || isReadOnly}
                        nux_number_id={task.id_nux_number}
                    />
                    {!isRecurringTaskTemplate &&
                        task.source?.name === GENERAL_TASK_SOURCE_NAME &&
                        !task.is_deleted &&
                        !task.optimisticId &&
                        !task.id_nux_number &&
                        !isMeetingPreparationTask && (
                            <TaskResearchCard key={task.id} task={taskv4} researchEnabled={researchEnabled} />
                        )}
                    {SOURCES_ALLOWED_WITH_SUBTASKS.includes(task.source?.name ?? '') &&
                        !task.is_deleted &&
                        isTaskParentTask(taskv4) && <SubtaskList parentTask={taskv4} />}
                </>
            )}
        </DetailsViewTemplate>
    )
}

export default TaskDetails
